import { css, cx } from "@emotion/css";
import { useState } from "react";
import { FiUser, FiEdit, FiCheck, FiX } from "react-icons/fi";
import { useUpdateProfile } from "../../hooks";
import type { Profile } from "../../../../api/services/profile/types";
import type { HTMLAttributes, CSSProperties } from "react";

/* Styles */
const profileSection = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 360px)': {
    padding: 'var(--spacing-sm)',
  },
});

const sectionHeader = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--spacing-sm)',
  },
});

const sectionTitle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const editBtn = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  backgroundColor: 'var(--g-100)',
  color: 'var(--pc-600)',
  border: 'none',
  borderRadius: 'var(--spacing-xs)',
  padding: 'var(--spacing-2xs) var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'var(--pc-100)',
  },
  '@media (max-width: 480px)': {
    alignSelf: 'flex-start',
  },
});

const editActions = css({
  display: 'flex',
  gap: 'var(--spacing-xs)',
  '@media (max-width: 360px)': {
    flexDirection: 'column',
    width: '100%',
  },
});

const saveBtn = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  backgroundColor: 'var(--pc-500)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--spacing-xs)',
  padding: 'var(--spacing-2xs) var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: 'var(--pc-600)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  '@media (max-width: 360px)': {
    width: '100%',
    justifyContent: 'center',
  },
});

const cancelBtn = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  backgroundColor: 'var(--g-100)',
  color: 'var(--g-700)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  padding: 'var(--spacing-2xs) var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: 'var(--g-200)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  '@media (max-width: 360px)': {
    width: '100%',
    justifyContent: 'center',
  },
});

const infoGrid = css({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 'var(--spacing-lg)',
  '@media (min-width: 96.8rem)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@media (min-width: 102.4rem)': {
    gap: 'var(--spacing-xl)',
  },
});

const infoItem = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-2xs)',
});

const infoLabel = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-500)',
  fontWeight: 500,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-300)',
  },
});

const infoValue = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-800)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

const infoInput = css({
  fontSize: 'var(--fs-400)',
  padding: 'var(--spacing-2xs) var(--spacing-xs)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  color: 'var(--g-800)',
  backgroundColor: 'var(--primary-color)',
  transition: 'border-color 0.2s ease',
  '&:focus': {
    outline: 'none',
    borderColor: 'var(--pc-400)',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-300), 0.2)',
  },
});

const alert = css({
  padding: 'var(--spacing-sm)',
  borderRadius: 'var(--spacing-xs)',
  fontSize: 'var(--fs-350)',
  marginBottom: 'var(--spacing-md)',
});

const alertSuccess = css({
  backgroundColor: 'rgba(72, 187, 120, 0.1)',
  color: 'rgb(39, 103, 73)',
  border: '1px solid rgba(72, 187, 120, 0.3)',
});

const alertError = css({
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  color: 'rgb(155, 44, 44)',
  border: '1px solid rgba(245, 101, 101, 0.3)',
});

/* Component Props */
export interface ProfileInfoProps extends HTMLAttributes<HTMLDivElement> {
  profile: Profile;
  style?: CSSProperties;
}

export function ProfileInfo({ profile, className, style, ...props }: ProfileInfoProps) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: profile.name || '',
    phone: profile.phone || '',
  });
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: updateProfile, isPending: updating } = useUpdateProfile();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setError(null);
      setUpdateSuccess(false);

      updateProfile(formData, {
        onSuccess: () => {
          setEditMode(false);
          setUpdateSuccess(true);

          // Hide success message after 3 seconds
          setTimeout(() => {
            setUpdateSuccess(false);
          }, 3000);
        },
        onError: (err) => {
          setError('Erro ao atualizar perfil');
          console.error(err);
        },
      });
    } catch (err) {
      setError('Erro ao atualizar perfil');
      console.error(err);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: profile.name || '',
      phone: profile.phone || '',
    });
    setEditMode(false);
    setError(null);
  };

  return (
    <section className={cx(profileSection, className)} style={style} {...props}>
      {error && <div className={cx(alert, alertError)}>{error}</div>}

      {updateSuccess && (
        <div className={cx(alert, alertSuccess)}>Perfil atualizado com sucesso!</div>
      )}

      <div className={sectionHeader}>
        <h2 className={sectionTitle}>
          <FiUser size={24} />
          Informações Pessoais
        </h2>
        {!editMode ? (
          <button className={editBtn} onClick={() => setEditMode(true)}>
            <FiEdit size={20} />
            <span>Editar</span>
          </button>
        ) : (
          <div className={editActions}>
            <button
              className={saveBtn}
              onClick={handleSaveProfile}
              disabled={updating}
            >
              {updating ? (
                <span>Salvando...</span>
              ) : (
                <>
                  <FiCheck size={20} />
                  <span>Salvar</span>
                </>
              )}
            </button>
            <button
              className={cancelBtn}
              onClick={handleCancelEdit}
              disabled={updating}
            >
              <FiX size={20} />
              <span>Cancelar</span>
            </button>
          </div>
        )}
      </div>

      <div className={infoGrid}>
        <div className={infoItem}>
          <span className={infoLabel}>Nome Completo</span>
          {editMode ? (
            <input
              type="text"
              name="name"
              className={infoInput}
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Seu nome completo"
            />
          ) : (
            <p className={infoValue}>{profile.name || 'Não informado'}</p>
          )}
        </div>
        <div className={infoItem}>
          <span className={infoLabel}>Email</span>
          <p className={infoValue}>{profile.email || 'Não informado'}</p>
        </div>
        <div className={infoItem}>
          <span className={infoLabel}>Telefone</span>
          {editMode ? (
            <input
              type="tel"
              name="phone"
              className={infoInput}
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Seu telefone"
            />
          ) : (
            <p className={infoValue}>{profile.phone || 'Não informado'}</p>
          )}
        </div>
        <div className={infoItem}>
          <span className={infoLabel}>CPF</span>
          <p className={infoValue}>{profile.cpf || 'Não informado'}</p>
        </div>
      </div>
    </section>
  );
}

export default ProfileInfo;
